import type { OrganizationType, SubscriptionType } from '@kanbu/schema';
import { SubscriptionStatus, SubscriptionTier } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { Badge, Button, toast } from '@utima/ui';
import { ExternalLink } from 'lucide-react';
import type React from 'react';

import { useSubscriptionManager } from '../hooks/useSubscriptionManager';

interface StatusDetails {
  label: string;
  variant: 'success' | 'danger' | 'warning' | 'info' | 'muted';
  description: React.ReactNode;
}

const getStatusDetails = (
  status: string | undefined | null,
  cancelsAtPeriodEnd: boolean | undefined | null,
  endDate: Date | string | undefined | null,
  locale: string,
): StatusDetails => {
  if (status === SubscriptionStatus.Active && cancelsAtPeriodEnd) {
    const formattedEndDate = endDate
      ? new Date(endDate).toLocaleDateString(locale)
      : 'the end of the period';

    return {
      label: 'Active (Cancels Soon)',
      variant: 'warning',
      description: (
        <Trans>
          We are sorry to see you go! Your subscription will automatically
          cancel on {formattedEndDate}. You can resume your subscription via the
          billing portal anytime before this date.
        </Trans>
      ),
    };
  }

  // Original status mapping
  switch (status) {
    case SubscriptionStatus.Active:
      return {
        label: 'Active',
        variant: 'success',
        description: (
          <Trans>Your subscription is active and billing normally.</Trans>
        ),
      };
    case SubscriptionStatus.PastDue:
      return {
        label: 'Payment Failed',
        variant: 'danger',
        description: (
          <Trans>
            Your latest payment failed. Please update your payment method to
            reactivate your subscription.
          </Trans>
        ),
      };
    case SubscriptionStatus.Canceled:
      return {
        label: 'Canceled',
        variant: 'warning',
        description: <Trans>Your subscription has been canceled.</Trans>,
      };
    case SubscriptionStatus.Incomplete:
      return {
        label: 'Payment Pending',
        variant: 'info',
        description: (
          <Trans>
            Your subscription requires payment. Please complete the payment
            process via the billing portal.
          </Trans>
        ),
      };
    case SubscriptionStatus.IncompleteExpired:
      return {
        label: 'Payment Required',
        variant: 'danger',
        description: (
          <Trans>
            Your previous payment attempt expired or failed. Please update your
            payment method via the billing portal.
          </Trans>
        ),
      };
    case SubscriptionStatus.Trialing:
      return {
        label: 'Trial',
        variant: 'info',
        description: <Trans>Your subscription is in trial period.</Trans>,
      };
    case SubscriptionStatus.Unpaid:
      return {
        label: 'Unpaid',
        variant: 'danger',
        description: (
          <Trans>
            Your subscription has unpaid invoices. Please update your payment
            method via the billing portal.
          </Trans>
        ),
      };

    default:
      return {
        label: 'Free',
        variant: 'muted',
        description: <Trans>You are currently on the free plan.</Trans>,
      };
  }
};

export function SubscriptionStatusDisplay({
  subscription,
  organization,
}: {
  subscription: Omit<SubscriptionType, 'organization'> | null;
  organization: OrganizationType;
}) {
  const { t, i18n } = useLingui();
  const orgId = organization.id;
  const isFreeTier =
    !subscription || subscription.tier === SubscriptionTier.Free;
  const currentTier = isFreeTier ? t`Free` : subscription?.tier;

  // Get status details considering the new flag and end date
  const statusDetails = isFreeTier
    ? getStatusDetails(null, null, null, i18n.locale)
    : getStatusDetails(
        subscription?.status,
        subscription?.cancelsAtPeriodEnd,
        subscription?.endDate,
        i18n.locale,
      );

  const { createBillingPortal, isLoading } = useSubscriptionManager(
    orgId ?? '',
  );

  const handleStripePortalClick = async () => {
    if (!orgId) return;
    try {
      const { url } = await createBillingPortal({});
      if (url) {
        window.open(url, '_blank');
      } else {
        toast.error(
          t`Failed to generate billing portal link. Please try again later.`,
        );
      }
    } catch (error) {
      console.error('Error opening billing portal:', error);
      toast.error(
        t`Unable to access billing portal. Please try again later or contact support.`,
      );
    }
  };

  // Determine if payment/management button should be shown
  const showPaymentButton =
    !isFreeTier && // Not Free
    organization.stripeCustomerId && // Has Stripe ID
    !subscription?.cancelsAtPeriodEnd && // Not scheduled for cancellation
    (subscription?.status === SubscriptionStatus.PastDue ||
      subscription?.status === SubscriptionStatus.Unpaid ||
      subscription?.status === SubscriptionStatus.Incomplete ||
      subscription?.status === SubscriptionStatus.IncompleteExpired);

  // Determine if resume button should be shown
  const showResumeButton =
    !isFreeTier &&
    organization.stripeCustomerId &&
    subscription?.status === SubscriptionStatus.Active &&
    subscription?.cancelsAtPeriodEnd;

  return (
    <div className='rounded-md border bg-gray-50 p-4'>
      <div className='flex items-center justify-between'>
        <div>
          <div className='text-sm text-gray-600'>
            <Trans>Current Plan</Trans>
          </div>
          <div className='text-lg font-semibold'>{currentTier}</div>
        </div>

        <Badge variant={statusDetails.variant}>{statusDetails.label}</Badge>
      </div>

      <div className='mt-2 text-sm text-gray-600'>
        {statusDetails.description}
      </div>

      {/* Conditional Payment Button */}
      {showPaymentButton && (
        <div className='mt-2 text-right'>
          <Button
            size='sm'
            variant={statusDetails.variant === 'info' ? 'secondary' : 'danger'}
            onClick={handleStripePortalClick}
            disabled={!orgId || isLoading}
          >
            <ExternalLink className='mr-1.5 size-4' />
            <Trans>Manage Billing / Payment</Trans>
          </Button>
        </div>
      )}

      {/* Conditional Resume Button */}
      {showResumeButton && (
        <div className='mt-2 text-right'>
          <Button
            data-cta='admin-subscription-status-display-resume-button'
            size='sm'
            variant='secondary' // Or another appropriate variant
            onClick={handleStripePortalClick}
            disabled={!orgId || isLoading}
          >
            <ExternalLink className='mr-1.5 size-4' />
            <Trans>Resume Subscription</Trans>
          </Button>
        </div>
      )}
    </div>
  );
}
